/*
  Kiwi Ears · cursor image trail (Rene 2026-08-23).

  A port of Originkit's `cursor-image-trail`, not a copy of it. The original is
  React + framer-motion holding the live trail in useState; this theme is Liquid
  and GSAP, and CLAUDE.md is explicit that a component from a React library gets
  translated rather than adopted. The behaviour and the tuned numbers are the
  original's:

    frequency 15  →  threshold = 200 - ((15-1) * 199) / 49  =  143px
    visibleFor 2  →  two seconds lit, then a two-second exit
    enter/exit    →  opacity 0→1, scale 0.5→1, blur 10px→0

  Two departures, both deliberate:

  · The trail drops CLONES of markup, not background-image URLs. The tile in
    Figma 412:2261 is a composed object — a green radial, a violet radial under
    it, a product cutout with two drop shadows and an outer glow — which a
    background-image cannot express. The tiles are rendered once by Liquid into
    a hidden pool and cloned from there, so the artwork is authored in one place
    and the script never builds DOM from strings.

  · React re-rendered the whole trail on every mousemove. Here each tile is an
    independent element animated by GSAP and removed on completion, so a fast
    pointer costs one clone per 143px rather than a render per pixel.

  Fine pointers only, and off entirely under prefers-reduced-motion: a trail
  that follows a cursor is meaningless without a cursor, and it is exactly the
  kind of incidental motion the reduced-motion setting exists to stop.
*/
(function () {
  if (window.__kiwiTrailReady) return;
  window.__kiwiTrailReady = true;

  var roots = [].slice.call(document.querySelectorAll('[data-trail]'));
  if (!roots.length) return;

  var mq = window.matchMedia;
  var still = mq && mq('(prefers-reduced-motion: reduce)').matches;
  var fine = mq ? mq('(hover: hover) and (pointer: fine)').matches : true;
  if (still || !fine) return;

  function arm() {
    var g = window.gsap;
    if (!g) return;

    roots.forEach(function (root) {
      var pool = root.querySelector('[data-trail-pool]');
      if (!pool) return;
      var tiles = [].slice.call(pool.children);
      if (!tiles.length) return;

      var layer = root.querySelector('[data-trail-layer]') || root;
      var freq = parseFloat(root.getAttribute('data-trail-frequency')) || 15;
      var lit = parseFloat(root.getAttribute('data-trail-visible')) || 2;
      /* the original's mapping, kept exactly so the feel matches the preview */
      var threshold = 200 - ((freq - 1) * 199) / 49;

      var next = 0, lastX = null, lastY = null, live = 0;
      /* A hard ceiling the original does not have. Its trail is bounded only by
         how fast a pointer can move; on a 100vh band a determined mouse can
         stack up dozens of blurred layers and the compositor starts dropping
         frames. Oldest goes first. */
      var MAX = 14;

      function drop(x, y) {
        var tile = tiles[next].cloneNode(true);
        next = (next + 1) % tiles.length;

        tile.removeAttribute('aria-hidden');
        tile.setAttribute('aria-hidden', 'true');
        tile.classList.add('kh-trail-item');
        layer.appendChild(tile);
        live++;

        var w = tile.offsetWidth || 188;
        var h = tile.offsetHeight || 185;

        g.set(tile, { x: x - w / 2, y: y - h / 2, opacity: 0, scale: 0.5, filter: 'blur(10px)' });
        g.to(tile, {
          opacity: 1, scale: 1, filter: 'blur(0px)',
          duration: 0.55, ease: 'back.out(1.4)'
        });
        g.to(tile, {
          opacity: 0, scale: 0.5, filter: 'blur(10px)',
          duration: 0.8, ease: 'power2.in', delay: lit,
          onComplete: function () { tile.remove(); live--; }
        });

        if (live > MAX) {
          var extra = layer.querySelector('.kh-trail-item');
          if (extra) { g.killTweensOf(extra); extra.remove(); live--; }
        }
      }

      root.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        var r = root.getBoundingClientRect();
        var x = e.clientX - r.left, y = e.clientY - r.top;
        var far = lastX === null || Math.hypot(x - lastX, y - lastY) > threshold;
        if (!far) return;
        lastX = x; lastY = y;
        drop(x, y);
      });

      /* Re-arm the distance gate on the way out, so coming back into the band
         drops a tile immediately instead of waiting to travel 143px again. */
      root.addEventListener('pointerleave', function () { lastX = lastY = null; });
    });
  }

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
